import {
	Badge,
	Inline,
	RelativeTime,
	Stack,
	Table,
	Text,
	Box,
	Icons,
	Button,
} from "@sembark-travel/ui-base"
import { SwitchInputField } from "@sembark-travel/ui-form"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { useLocationQuery, Link } from "@sembark-travel/ui-router"
import { useEffect, useState } from "react"
import { TAutomationRun, XHR } from "./store"
import { RunTraceDialog } from "./RunTraceDialog"
import { RetryRunDialog } from "./RetryRunDialog"
import { isTruthy } from "remeda"
import { generatePath } from "../router-utils"

type TFilters = TSearchParams & {
	failed_only?: boolean
	dry_run_only?: boolean
}

type TFiltersInQuery = TSearchParams & {
	status?: "failed"
	run_mode?: "dry_run"
}

export function FormatRunContext({
	run,
}: {
	run: {
		triggered_by_type?: string | null
		triggered_by_id?: number | null
		trigger?: { wa_thread_id?: number | null } | null
	}
}) {
	if (!run.triggered_by_type || !run.triggered_by_id) {
		return "System"
	}

	// A run triggered from inside a WhatsApp conversation — by the thread
	// itself or by a message in it — links to that conversation.
	if (run.trigger?.wa_thread_id) {
		return (
			<Link
				to={generatePath("/whatsapp/inbox/:threadId", {
					threadId: run.trigger.wa_thread_id.toString(),
				})}
				color="accent"
			>
				{run.triggered_by_type === "wa_messages"
					? `Reply #${run.triggered_by_id}`
					: `Conversation #${run.triggered_by_id}`}
			</Link>
		)
	}

	if (run.triggered_by_type === "instalments") {
		return (
			<Link
				to={generatePath("/payments/instalments/:instalmentId", {
					instalmentId: run.triggered_by_id.toString(),
				})}
				color="accent"
			>
				Instalment #{run.triggered_by_id}
			</Link>
		)
	}

	return `${run.triggered_by_type} #${run.triggered_by_id}`
}

function formatRetryMode(run: TAutomationRun) {
	if (run.retry?.mode === "resume_from_failed_step") {
		return "Resumed from failed step"
	}

	if (run.retry?.mode === "start_over") {
		return "Started over"
	}

	return null
}

function formatRunMode(run: TAutomationRun) {
	return run.run_mode === "dry_run" ? "Dry Run" : "Live"
}

function runProvenanceLines(run: TAutomationRun) {
	const lines: Array<string> = []
	const eventName = run.event_name || run.provenance?.event?.name
	const changedFields =
		run.event_changed_fields || run.provenance?.event?.changed_fields || []
	const schedule = run.provenance?.schedule

	if (eventName) {
		lines.push(`Event: ${eventName}`)
	}

	if (changedFields.length) {
		lines.push(`Changed: ${changedFields.join(", ")}`)
	}

	if (schedule?.checkpoint_key) {
		lines.push(`Checkpoint: ${schedule.checkpoint_key}`)
	}

	if (schedule?.automation_scheduled_run_id) {
		lines.push(`Schedule #${schedule.automation_scheduled_run_id}`)
	}

	if (typeof schedule?.occurrence_index === "number") {
		lines.push(`Occurrence ${schedule.occurrence_index}`)
	}

	return lines
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const { failed_only, dry_run_only, q, page } = filters
	const query: TFiltersInQuery = {}

	if (failed_only) {
		query.status = "failed"
	}

	if (dry_run_only) {
		query.run_mode = "dry_run"
	}

	if (q) {
		query.q = q
	}

	if (page) {
		query.page = page
	}

	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const { status, run_mode, q, page } = query
	const filters: TFilters = {}

	if (status === "failed") {
		filters.failed_only = true
	}

	if (run_mode === "dry_run") {
		filters.dry_run_only = true
	}

	if (q) {
		filters.q = q
	}

	if (page) {
		filters.page = page
	}

	return filters
}

function filtersToRequestParams(filters: TFilters): TSearchParams & {
	status?: "failed"
	run_mode?: "dry_run"
} {
	const { failed_only, dry_run_only, ...otherFilters } = filters

	return {
		...otherFilters,
		...(failed_only ? { status: "failed" as const } : {}),
		...(dry_run_only ? { run_mode: "dry_run" as const } : {}),
	}
}

export function AutomationRunsList({
	automationIds,
	title = "Automation Runs",
	pageKey = "automation-runs",
}: {
	automationIds?: Array<number>
	title?: string
	pageKey?: string
} = {}) {
	const [query, setQuery] = useLocationQuery<TFilters, TFiltersInQuery>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)
	const [refreshKey, setRefreshKey] = useState(0)

	useEffect(() => {
		setQuery(params)
	}, [setQuery, params])

	return (
		<Search
			title={title}
			initialParams={params}
			onSearch={(nextParams) => setParams({ ...nextParams, page: 1 })}
			resetParams={{ q: "", page: 1 }}
			areAdvancedFiltersApplied={(params) => {
				const { failed_only, dry_run_only, ...others } = params
				return (
					isTruthy(failed_only) ||
					isTruthy(dry_run_only) ||
					areAdvancedFiltersAppliedDefault(others)
				)
			}}
			Filters={Filters}
		>
			<ListView<TAutomationRun>
				pageKey={`${pageKey}-${refreshKey}`}
				params={params}
				onPageChange={(page) => setParams({ ...params, page })}
				fetch={(xhr, listParams) =>
					XHR(xhr).getRuns({
						...filtersToRequestParams(listParams),
						automations: automationIds,
					})
				}
			>
				{({ items }) => (
					<Table
						bordered
						hover
						responsive
						headers={[
							"Automation",
							"Context",
							"Status",
							"Started",
							"Finished",
							"",
						]}
						alignCols={{ 5: "right" }}
						rows={items.map((run) => [
							<Stack gap="1" key={run.id}>
								<Text fontWeight="semibold">
									{run.automation?.source?.name ||
										run.automation?.source_key ||
										run.source_key}
								</Text>
								<Text color="muted" fontSize="sm">
									Run #{run.id}
								</Text>
								{run.retry?.retried_from_run_id ? (
									<Text color="muted" fontSize="sm">
										{formatRetryMode(run) || "Retried"} from Run #
										{run.retry.retried_from_run_id}
									</Text>
								) : null}
							</Stack>,
							<Stack gap="1">
								<Box>
									<FormatRunContext run={run} />
								</Box>
								{runProvenanceLines(run).map((line) => (
									<Text key={`${run.id}-${line}`} color="muted" fontSize="sm">
										{line}
									</Text>
								))}
							</Stack>,
							<Stack gap="1">
								<Inline flexWrap="wrap" gap="1">
									<Badge
										success={run.status === "completed"}
										warning={
											run.status === "pending" || run.status === "running"
										}
										danger={
											run.status === "failed" || run.status === "cancelled"
										}
										title={
											run.status === "failed"
												? run.error || undefined
												: undefined
										}
									>
										{run.status}
									</Badge>
									{run.run_mode !== "live" ? (
										<Badge warning={run.run_mode === "dry_run"}>
											{formatRunMode(run)}
										</Badge>
									) : null}
								</Inline>
								<Text color="muted" fontSize="sm">
									{run.step_runs_count || 0} step runs
								</Text>
							</Stack>,
							run.started_at ? (
								<RelativeTime timestamp={run.started_at} />
							) : (
								<Text color="muted">Not started</Text>
							),
							run.completed_at || run.failed_at || run.cancelled_at ? (
								<RelativeTime
									timestamp={
										run.completed_at ||
										run.failed_at ||
										run.cancelled_at ||
										undefined
									}
								/>
							) : run.resume_at ? (
								<Stack gap="1">
									<Inline gap="1" alignItems="center" flexWrap="wrap">
										<Text color="muted" fontSize="sm">
											Resumes
										</Text>
										<RelativeTime timestamp={run.resume_at} />
									</Inline>
									{run.current_step_position ? (
										<Text color="muted" fontSize="sm">
											at step {run.current_step_position}
										</Text>
									) : null}
								</Stack>
							) : (
								<Text color="muted">In progress</Text>
							),
							<Inline gap="2" justifyContent="end">
								<RetryRunDialog
									run={run}
									onSuccess={() => setRefreshKey((current) => current + 1)}
								/>
								<RunTraceDialog run={run}>
									{({ open }) => (
										<Button
											onClick={open}
											size="sm"
											level="tertiary"
											title="View Details/Traces"
										>
											<Icons.ArrowExpand />
										</Button>
									)}
								</RunTraceDialog>
							</Inline>,
						])}
					/>
				)}
			</ListView>
		</Search>
	)
}

function Filters() {
	return (
		<Stack gap="3">
			<SwitchInputField name="failed_only" label="Failed Runs Only" />
			<SwitchInputField name="dry_run_only" label="Dry Runs Only" />
		</Stack>
	)
}
